import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Bot, CandleType, Scale } from '../../types';
import './index.css';

import ChartOptions from './ChartOptions';
import LoadingIcons from 'react-loading-icons';
import { PriceChartApiResponse } from '..';
import { formatPrice } from '../../../../helpers/NumberFormatter';
import { useNavigate } from 'react-router-dom';
import { getSelectedTimeFrame } from './helpers';
import CandleSticksChart from '../../../../components/Charts/CandleSticks';
import { ComponentDimensions } from '../../../../types/Layout';
import { CandleSticksLatestOrder } from '../types';

interface Props {
  bot: Bot;
  candles: PriceChartApiResponse | undefined;
  selectedTimeFrameIndex: number;
  setSelectedTimeFrameIndex: React.Dispatch<React.SetStateAction<number>>;
  isLoading: boolean;
  candleSticksLatestOrder?: CandleSticksLatestOrder;
}

const PriceChart: React.FC<Props> = (props: Props) => {
  const {
    bot,
    selectedTimeFrameIndex,
    candles,
    setSelectedTimeFrameIndex,
    isLoading,
    candleSticksLatestOrder,
  } = props;
  const { timeFrame } = bot;
  const navigate = useNavigate();

  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(undefined);
  const rootRef = useRef<any>(null);

  const selectedTimeFrame = getSelectedTimeFrame(bot, selectedTimeFrameIndex);
  const [candleType, setCandleType] = useState(CandleType.Heikin);
  const [selectedScale, setSelectedScale] = useState(Scale.Logarithmic);
  const [useParentIndicators, setUseParentIndicators] = useState(false);

  useEffect(() => {
    if (!!rootDimensions) {
      return;
    }

    if (!rootRef.current) {
      return;
    }

    setRootDimensions({
      height: rootRef.current.clientHeight,
      width: rootRef.current.clientWidth,
    });
  }, [rootDimensions]);

  const handleChartPress = useCallback(() => {
    navigate('/profitCalculations', {
      state: {
        baseAsset: bot.baseAsset,
        quoteAsset: bot.quoteAsset,
        timeFrame: selectedTimeFrame,
        scale: selectedScale,
        candleType,
        bot,
        candleSticksLatestOrder,
      },
    });
  }, [
    bot,
    candleSticksLatestOrder,
    candleType,
    navigate,
    selectedScale,
    selectedTimeFrame,
  ]);

  const currentPrice = useMemo(() => {
    const currentPriceValue =
      candles?.normal?.[candles?.normal?.length - 1]?.close;

    if (!currentPriceValue) {
      return undefined;
    }

    return formatPrice(currentPriceValue, true, 2);
  }, [candles?.normal]);

  const timeFrames = useMemo(() => {
    if (!timeFrame) {
      return [];
    }

    return [timeFrame.multiOrder, timeFrame.shorter, timeFrame.higher];
  }, [timeFrame]);

  if (!selectedTimeFrame) {
    return null;
  }

  return (
    <div className="pc-container" ref={rootRef}>
      {rootDimensions && (
        <div className="pc-container-chart" onClick={handleChartPress}>
          <CandleSticksChart
            baseAsset={bot.baseAsset}
            quoteAsset={bot.quoteAsset}
            timeFrame={selectedTimeFrame}
            exchange={bot.defaultExchange}
            candleType={candleType}
            candleResponse={candles}
            isLoading={isLoading}
            dimensions={rootDimensions}
            scale={selectedScale}
            currentPrice={currentPrice}
            // bot={bot}
            candleSticksLatestOrder={candleSticksLatestOrder}
            chartOptionsComponent={
              <ChartOptions
                candleType={candleType}
                setCandleType={setCandleType}
                timeFrames={timeFrames}
                selectedTimeFrameIndex={selectedTimeFrameIndex}
                setSelectedTimeFrameIndex={setSelectedTimeFrameIndex}
                selectedScale={selectedScale}
                setSelectedScale={setSelectedScale}
                useParentIndicators={useParentIndicators}
                setUseParentIndicators={setUseParentIndicators}
              />
            }
            handleChartPress={handleChartPress}
            shouldFetch={true}
            useParentIndicators={useParentIndicators}
          />
        </div>
      )}

      {isLoading && (
        <div className="pc-loading-container">
          <LoadingIcons.Grid width={40} />
        </div>
      )}
    </div>
  );
};

export default React.memo(PriceChart);
